import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Broadcast, ArrowRight, CaretRight, CaretLeft, CalendarBlank, Clock as ClockIcon, ArrowSquareOut, CheckCircle, FlagBanner, AirplaneTilt, Pause, Play, UsersThree } from "@phosphor-icons/react";
import { getCounsellingLiveBoards, getCounsellingArchivedBoards } from "../lib/api";
import { SubscribeButton } from "./SubscribeButton";

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const DEFAULT_PALETTE = ["#3b82f6"];

const fmtDate = (iso) => {
  if (!iso) return "";
  const d = new Date(iso + "T00:00:00");
  if (isNaN(d)) return iso;
  return `${d.getDate()} ${MONTHS[d.getMonth()]} ${d.getFullYear()}`;
};
const rangeLabel = (e) => {
  const s = e.start, en = e.end || e.start;
  if (!s) return e.date || "—";
  if (!en || en === s) return fmtDate(s);
  return `${fmtDate(s)} → ${fmtDate(en)}`;
};
const addDays = (iso, n) => {
  const d = new Date(iso + "T00:00:00");
  d.setDate(d.getDate() + n);
  return d.toISOString().slice(0, 10);
};
const daysBetween = (a, b) => Math.round((new Date(b + "T00:00:00") - new Date(a + "T00:00:00")) / 86400000);
const fmtCount = (n) => (n >= 1000 ? `${(n / 1000).toFixed(1)}k` : `${n}`);

// Darken a hex colour for the gradient's second stop.
function shade(hex, amt) {
  const m = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex || "");
  if (!m) return hex;
  const adj = (v) => Math.max(0, Math.min(255, Math.round(parseInt(v, 16) * (1 + amt))));
  const h = (n) => n.toString(16).padStart(2, "0");
  return `#${h(adj(m[1]))}${h(adj(m[2]))}${h(adj(m[3]))}`;
}

// The currently-active timeline item for a body, relative to refDate (ISO).
function activeItem(events, refDate) {
  const dated = (events || []).filter((e) => e.start);
  if (!dated.length) return { event: null, status: "completed" };
  const cur = dated.find((e) => e.start <= refDate && (e.end || e.start) >= refDate);
  if (cur) return { event: cur, status: "live" };
  const next = dated.find((e) => e.start > refDate);
  if (next) return { event: next, status: "upcoming" };
  return { event: dated[dated.length - 1], status: "completed" };
}

// Apply CTA visible while the application/registration window hasn't closed.
function applyOpen(body, refDate) {
  if (!body.apply_link) return null;
  const appEvt = (body.events || []).find((e) => e.link && (e.end || e.start || "9999") >= refDate);
  return appEvt ? body.apply_link : null;
}

const useClock = () => {
  const [t, setT] = useState(new Date());
  useEffect(() => { const id = setInterval(() => setT(new Date()), 1000); return () => clearInterval(id); }, []);
  return t.toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit", second: "2-digit" });
};

export function CounsellingBoard() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [archived, setArchived] = useState(null);
  const [tab, setTab] = useState(0);
  const [view, setView] = useState("live");  // "live" | "archived"
  const clock = useClock();

  useEffect(() => {
    const fetchIt = () => {
      getCounsellingLiveBoards().then((r) => setData(r.data)).catch(() => {});
      getCounsellingArchivedBoards().then((r) => setArchived(r.data?.boards || [])).catch(() => setArchived([]));
    };
    fetchIt();
    const id = setInterval(fetchIt, 60000);
    return () => clearInterval(id);
  }, []);

  if (!data) return null;
  // Order tabs UG → PG → others (stable for anything else). The board's exam/key/tab
  // text carries the level, e.g. "NEET UG 2026".
  const boardRank = (b) => {
    const s = `${b.exam || ""} ${b.key || ""} ${b.tab || ""}`.toUpperCase();
    if (/\bUG\b|UG[_-]/.test(s)) return 0;
    if (/\bPG\b|PG[_-]/.test(s)) return 1;
    return 2;
  };
  // Hide a board whose rows would ALL be filtered out (e.g. a schedule whose events
  // have no parsed dates → every row counts as "completed"), so we never render an
  // empty header/tab. Demo boards always show.
  const _today = new Date().toISOString().slice(0, 10);
  const hasVisibleRows = (b) =>
    b.demo || (b.bodies || []).some((body) => !body.dummy && activeItem(body.events, _today).status !== "completed");
  const boards = [...(data.boards || [])].sort((a, b) => boardRank(a) - boardRank(b)).filter(hasVisibleRows);
  const hasLive = boards.length > 0;
  const arch = archived || [];
  const hasArchived = arch.length > 0;
  if (!hasLive && !hasArchived) return null; // nothing to show
  // Default the active view to whichever exists (live preferred).
  const activeView = view === "archived" ? (hasArchived ? "archived" : "live") : (hasLive ? "live" : "archived");
  const idx = Math.min(tab, Math.max(0, boards.length - 1));
  const board = hasLive ? boards[idx] : null;

  return (
    <section className="relative overflow-hidden border-b border-zinc-200"
      style={{ background: "linear-gradient(120deg,#EEF6FF 0%,#F5FFF9 55%,#EEF6FF 100%)" }}
      data-testid="counselling-board">
      <div aria-hidden className="absolute inset-0 pointer-events-none overflow-hidden">
        <div className="absolute -top-16 left-[10%] w-72 h-72 rounded-full bg-sky-300/40 blur-3xl" style={{ animation: "blobFloat 10s ease-in-out infinite" }} />
        <div className="absolute top-4 right-[12%] w-80 h-80 rounded-full bg-emerald-200/40 blur-3xl" style={{ animation: "blobFloat 13s ease-in-out infinite reverse" }} />
      </div>

      {/* LIVE / ARCHIVED top-border tab strip */}
      <div className="relative max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-[120px]">
        <div className="flex items-stretch" data-testid="board-view-tabs">
          <button data-testid="board-view-live" onClick={() => setView("live")}
            className={`px-5 py-2 text-[11px] font-black uppercase tracking-[0.18em] border-b-2 transition-colors inline-flex items-center gap-1.5 ${activeView === "live" ? "border-[#6043EF] text-[#6043EF] bg-white/70" : "border-transparent text-zinc-500 hover:text-zinc-700"}`}>
            <Broadcast size={13} weight="fill" className={activeView === "live" ? "text-emerald-600" : ""} /> Live
          </button>
          <span aria-hidden className="w-px my-2 bg-zinc-300/70" />
          <button data-testid="board-view-archived" onClick={() => setView("archived")}
            className={`px-5 py-2 text-[11px] font-black uppercase tracking-[0.18em] border-b-2 transition-colors inline-flex items-center gap-1.5 ${activeView === "archived" ? "border-[#6043EF] text-[#6043EF] bg-white/70" : "border-transparent text-zinc-500 hover:text-zinc-700"}`}>
            <FlagBanner size={13} weight="fill" /> Archived{hasArchived ? ` (${arch.length})` : ""}
          </button>
        </div>
      </div>

      {activeView === "archived" ? (
        <ArchivedTable rows={arch} clock={clock} onOpen={(r) => r.schedule_page && navigate(r.schedule_page)} />
      ) : (
      <div className="relative max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-[120px] py-5">
        {/* header */}
        <div className="flex items-start justify-between gap-3 mb-3">
          <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
            <AirplaneTilt size={26} weight="fill" className="text-[#6043EF]" style={{ animation: "blobFloat 4s ease-in-out infinite" }} />
            <h2 className="text-base sm:text-lg font-bold tracking-tight text-[#09090B]" data-testid="board-heading">
              {board.label}
            </h2>
            <span className="inline-flex items-center gap-1 text-[10px] font-black uppercase tracking-[0.2em] text-emerald-600">
              <Broadcast size={12} weight="fill" className="animate-pulse" /> Live
            </span>
            <span className="font-mono text-xs text-zinc-500 tabular-nums">{clock} IST</span>
          </div>

          {/* Animated subscribe ticket — top-right within section margins */}
          {!board.demo && (
            <div className="shrink-0 relative animate-ticket-float" data-testid="board-subscribe-ticket">
              {/* notch/perforation dots on the left edge */}
              <span aria-hidden className="absolute -left-1.5 top-1/2 -translate-y-1/2 flex flex-col gap-1">
                <span className="w-2 h-2 rounded-full bg-[#F7F5FF]" />
                <span className="w-2 h-2 rounded-full bg-[#F7F5FF]" />
                <span className="w-2 h-2 rounded-full bg-[#F7F5FF]" />
              </span>
              <div className="relative overflow-hidden rounded-lg border-2 border-orange-300 bg-gradient-to-br from-orange-50 to-amber-50 shadow-[3px_3px_0px_0px_rgba(234,88,12,0.35)] px-3 py-2 text-right">
                {/* shine sweep */}
                <span aria-hidden className="absolute inset-0 pointer-events-none">
                  <span className="absolute top-0 -left-1/3 w-1/3 h-full bg-white/40 skew-x-[-18deg]" style={{ animation: "shine 3.5s ease-in-out infinite" }} />
                </span>
                <SubscribeButton scope={board.scope} variant="light" accent="orange"
                  className="!bg-transparent !shadow-none !text-orange-600 !px-0 !h-auto !font-black !text-sm !uppercase !tracking-wider hover:!bg-transparent"
                  label="Subscribe"
                  bellSize={22}
                  bellClassName="animate-bell-ring text-orange-500"
                  subscribedLabel="Subscribed ✓" />
                <div className="mt-0.5 font-mono text-[10px] font-bold tracking-tight text-[#09090B]/70 lowercase">
                  {board.tab} · {board.kind_label}
                </div>
                {board.subscriber_count > 0 && (
                  <div className="mt-1 flex items-center justify-end gap-1 text-[10px] font-bold text-orange-600" data-testid="board-subscriber-count">
                    <UsersThree size={13} weight="fill" className="text-orange-500" />
                    {fmtCount(board.subscriber_count)} getting {board.kind_label} alerts
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        {/* tabs — rounded folder-style tabs sitting on the board's top line */}
        {boards.length > 1 && (
          <div className="flex flex-wrap items-end gap-1.5 pl-3 -mb-px relative z-10" data-testid="board-tabs">
            {boards.map((b, i) => (
              <button key={b.key} onClick={() => setTab(i)} data-testid={`board-tab-${b.key}`}
                className={`h-9 px-4 text-[11px] font-bold uppercase tracking-wider flex items-center gap-1.5 rounded-t-xl border border-b-0 transition-all ${i === idx ? "bg-[#6043EF] text-white border-[#6043EF] shadow-lg shadow-[#6043EF]/25" : "bg-white/70 text-zinc-600 border-zinc-200 hover:text-[#6043EF] hover:border-[#6043EF]"}`}>
                {b.kind === "exam" ? "🎓" : "🏛️"} {b.tab} · {b.kind_label}
              </button>
            ))}
          </div>
        )}

        <LiveBoard board={board} config={data.board_config}
          onOpen={(row) => row.body_slug && navigate(`/counselling/schedule/${row.body_slug}/${row.slug}`)}
          onData={(url) => navigate(url)} />
      </div>
      )}

      <style>{`
        @keyframes blobFloat{0%,100%{transform:translateY(0)}50%{transform:translateY(-14px)}}
        @keyframes floatY{0%,100%{transform:translateY(0)}50%{transform:translateY(-3px)}}
        @keyframes shine{0%{transform:translateX(-120%)}60%,100%{transform:translateX(220%)}}
        @keyframes pulseDot{0%,100%{transform:scale(1);opacity:1}50%{transform:scale(1.6);opacity:.5}}
        @keyframes rowShine{0%{transform:translateX(-60%) skewX(-18deg)}100%{transform:translateX(560%) skewX(-18deg)}}
        @keyframes boardPulse{0%,100%{filter:brightness(1)}50%{filter:brightness(1.12)}}
        @keyframes flapIn{0%{transform:rotateX(-90deg);opacity:0}100%{transform:rotateX(0);opacity:1}}
        @keyframes autoprog{0%{width:0%}100%{width:100%}}
        @keyframes ticketSway{0%,100%{transform:rotate(-1.5deg)}50%{transform:rotate(1.5deg)}}
      `}</style>
    </section>
  );
}

function ArchivedTable({ rows, clock, onOpen }) {
  return (
    <div className="relative max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-[120px] py-5" data-testid="archived-board">
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mb-3">
        <FlagBanner size={24} weight="fill" className="text-zinc-400" />
        <h2 className="text-base sm:text-lg font-bold tracking-tight text-[#09090B]" data-testid="archived-heading">
          Archived Counselling Schedules
        </h2>
        <span className="inline-flex items-center gap-1 text-[10px] font-black uppercase tracking-[0.2em] text-zinc-500">
          <ClockIcon size={12} weight="fill" /> Past
        </span>
        <span className="font-mono text-xs text-zinc-500 tabular-nums">{clock} IST</span>
      </div>

      <div className="rounded-2xl border border-zinc-200 bg-white/80 backdrop-blur overflow-hidden shadow-sm">
        <div className="hidden sm:grid grid-cols-[0.7fr_1.9fr_1.4fr_1.6fr] gap-2 px-4 py-2 bg-zinc-900 text-[10px] font-black uppercase tracking-[0.15em] text-zinc-300">
          <span>Body</span><span>Name</span><span>Window</span><span>Schedule &amp; Links</span>
        </div>
        <div className="divide-y divide-zinc-100">
          {rows.map((r) => (
            <div key={`${r.body_slug}-${r.slug}`} data-testid={`archived-row-${r.short}`}
              className="px-3 sm:px-4 py-3 block sm:grid sm:grid-cols-[0.7fr_1.9fr_1.4fr_1.6fr] sm:gap-2 sm:items-center hover:bg-sky-50/60 transition-colors">

              {/* Body short */}
              <span className="block text-sm font-black tracking-tight text-[#6043EF]">{r.short}</span>

              {/* Full name + tags */}
              <span className="block mt-1 sm:mt-0 min-w-0">
                <span className="block text-sm font-bold text-[#09090B] truncate">{r.body}</span>
                <span className="block text-[11px] text-zinc-400">
                  {r.state ? `${r.state} · ` : ""}{r.stream_label} {r.kind_label} {r.year}
                </span>
              </span>

              {/* Window */}
              <span className="block mt-1.5 sm:mt-0 text-[11px] font-mono tabular-nums text-zinc-600">
                <span className="inline-flex items-center gap-1">
                  <CalendarBlank size={12} weight="bold" className="text-[#6043EF]" />
                  {r.start ? fmtDate(r.start) : "—"} → {r.end ? fmtDate(r.end) : "—"}
                </span>
                {r.event_count ? <span className="block mt-0.5 text-[10px] text-zinc-400">{r.event_count} events</span> : null}
              </span>

              {/* Schedule + all counselling links */}
              <span className="flex flex-wrap items-center gap-1.5 mt-1.5 sm:mt-0">
                {r.schedule_page && (
                  <button onClick={() => onOpen(r)} data-testid={`archived-schedule-${r.short}`}
                    className="inline-flex items-center gap-1 h-7 px-3 rounded-full text-[10px] font-black uppercase tracking-wider bg-[#6043EF] text-white shadow shadow-[#6043EF]/30 transition-transform hover:scale-105">
                    Schedule <CaretRight size={11} weight="bold" />
                  </button>
                )}
                {r.apply_link && (
                  <a href={r.apply_link} target="_blank" rel="noreferrer" data-testid={`archived-apply-${r.short}`}
                    className="inline-flex items-center gap-1 h-7 px-3 rounded-full text-[10px] font-black uppercase tracking-wider bg-white text-zinc-700 border border-zinc-200 hover:border-[#6043EF] transition-colors">
                    Apply <ArrowSquareOut size={11} weight="bold" />
                  </a>
                )}
                {(r.links || []).map((l, li) => (
                  <a key={li} href={l.url} target="_blank" rel="noreferrer" title={l.label || l.url}
                    className="inline-flex items-center gap-1 h-7 px-3 rounded-full text-[10px] font-bold capitalize bg-white text-zinc-600 border border-zinc-200 hover:border-[#6043EF] transition-colors">
                    {l.kind || "link"} <ArrowSquareOut size={10} weight="bold" />
                  </a>
                ))}
                {r.source_url && (
                  <a href={r.source_url} target="_blank" rel="noreferrer" data-testid={`archived-source-${r.short}`}
                    className="inline-flex items-center gap-1 h-7 px-3 rounded-full text-[10px] font-bold bg-white text-zinc-500 border border-zinc-200 hover:border-[#6043EF] transition-colors">
                    PDF <ArrowSquareOut size={10} weight="bold" />
                  </a>
                )}
              </span>
            </div>
          ))}
          {!rows.length && (
            <div className="px-4 py-8 text-center text-sm text-zinc-400" data-testid="archived-empty">
              No archived schedules yet.
            </div>
          )}
        </div>
      </div>
    </div>
  );
}


function LiveBoard({ board, config, onOpen, onData }) {
  const [tick, setTick] = useState(0);        // demo sim clock (seconds → days)
  const [wave, setWave] = useState(0);        // waterfall cascade
  const [page, setPage] = useState(0);
  const [paused, setPaused] = useState(false);       // hover/focus pause
  const [userPaused, setUserPaused] = useState(true); // manual auto-scroll (default paused)
  const demo = !!board.demo;

  const range = useMemo(() => {
    const starts = board.bodies.map((b) => b.sched_start).filter(Boolean).sort();
    const ends = board.bodies.map((b) => b.sched_end).filter(Boolean).sort();
    return { start: starts[0] || null, end: ends[ends.length - 1] || null };
  }, [board]);

  const palette = (config?.waterfall_colors?.length ? config.waterfall_colors : DEFAULT_PALETTE);
  const glaze = config?.glaze !== false;
  const speed = config?.speed_ms || 500;
  // Waterfall wave colours = the configured palette AS-IS (e.g. a single blue). The
  // wave lights ONE row (per colour) that sweeps top→bottom; every other live row
  // rests on the off-white background. Off-white must therefore stay visible.
  const waveColors = palette;
  const waveBg = waveColors.map((c) => `linear-gradient(100deg, ${c}, ${shade(c, -0.16)})`);
  const offwhite = config?.offwhite || "#f5fff9";
  const offwhiteBg = `linear-gradient(100deg, ${offwhite}, ${shade(offwhite, -0.05)})`;

  // Demo boards simulate a clock: 1 real second = 1 day, looping across the range.
  useEffect(() => {
    if (!demo) return;
    const id = setInterval(() => setTick((t) => t + 1), 1000);
    return () => clearInterval(id);
  }, [demo]);
  // Waterfall: shift each live row's colour to the one below it every `speed` ms.
  useEffect(() => {
    const id = setInterval(() => setWave((w) => w + 1), speed);
    return () => clearInterval(id);
  }, [speed]);

  const today = new Date().toISOString().slice(0, 10);
  let refDate = today;
  if (demo && range.start && range.end) {
    const span = Math.max(1, daysBetween(range.start, range.end));
    refDate = addDays(range.start, tick % (span + 4));
  }

  // One row per body = its active timeline item. Dummy bodies → "scheduled"
  // (awaiting counselling). Completed rows are hidden entirely.
  const rows = board.bodies
    .map((b) => {
      if (b.dummy) return { ...b, status: "scheduled", event: null, apply: null };
      const { event, status } = activeItem(b.events, refDate);
      const apply = applyOpen(b, refDate);
      const applyExt = (b.apply_link_external && b.apply_link_external !== apply) ? b.apply_link_external : null;
      return { ...b, status, event, apply, applyExt };
    })
    .filter((r) => r.status !== "completed" && !r.dummy);  // only bodies with a real schedule
  const ORDER = { live: 0, upcoming: 1, scheduled: 2 };
  rows.sort((a, b) => (ORDER[a.status] - ORDER[b.status]) || String(a.short).localeCompare(String(b.short)));

  const PAGE = 8;
  const pages = Math.max(1, Math.ceil(rows.length / PAGE));
  const p = Math.min(page, pages - 1);
  const slice = rows.slice(p * PAGE, p * PAGE + PAGE);

  // Auto-advance carousel through pages (kiosk), paused on hover/focus or by user.
  useEffect(() => {
    if (pages <= 1 || paused || userPaused) return;
    const id = setInterval(() => setPage((cur) => (cur + 1) % pages), 8000);
    return () => clearInterval(id);
  }, [pages, paused, userPaused]);

  // Sliding waterfall window across the live rows in the current slice.
  let liveCount = 0;
  const liveOrdinal = slice.map((r) => (r.status === "live" ? liveCount++ : -1));

  return (
    <div data-testid="live-board"
      onMouseEnter={() => setPaused(true)} onMouseLeave={() => setPaused(false)}
      onFocusCapture={() => setPaused(true)} onBlurCapture={() => setPaused(false)}>
      {demo && (
        <div className="flex items-center gap-2 mb-2 text-[11px] font-semibold text-zinc-500" data-testid="demo-clock">
          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-amber-100 text-amber-700 font-black uppercase tracking-wider text-[9px]">Demo · simulating</span>
          Playing the {board.year} schedule — <b className="text-[#6043EF] font-mono">{fmtDate(refDate)}</b> <span className="text-zinc-400">(1 sec = 1 day)</span>
        </div>
      )}

      <style>{`@keyframes stockBlink{0%,49.9%{background:${palette[0]};color:#ffffff}50%,100%{background:#ffffff;color:#0f172a}}`}</style>
      <div className="rounded-2xl border border-zinc-200 bg-white/80 backdrop-blur overflow-hidden shadow-sm">
        <div className="hidden sm:grid grid-cols-[1.4fr_0.7fr_1.7fr_1.25fr_1.1fr] gap-2 px-4 py-2 bg-zinc-900 text-[10px] font-black uppercase tracking-[0.15em] text-zinc-300">
          <span>Board</span><span>{board.kind === "exam" ? "Phase" : "Round"}</span><span>Status</span><span>Window</span><span>{board.kind === "exam" ? "Apply / Docs" : "Documents"}</span>
        </div>
        <div className="divide-y divide-zinc-100">
          {slice.map((r, i) => {
            const isLive = r.status === "live";
            // 2+ live rows → ONE blue row sweeps top→bottom (the wave); the rest rest on
            // off-white. A single live row keeps the stock blink (never both animations).
            const cascade = isLive && liveCount >= 2;
            const blink = isLive && liveCount < 2;
            const style = { transformOrigin: "top" };
            let waveOn = false;  // this cascade row is currently lit by the wave
            if (cascade) {
              const ord = liveOrdinal[i];
              const front = ((wave % liveCount) + liveCount) % liveCount;  // wave head, moves down
              const rel = ((ord - front) % liveCount + liveCount) % liveCount;  // 0 = at the head
              waveOn = rel < waveColors.length;
              style.background = waveOn ? waveBg[rel] : offwhiteBg;
              style.color = "#09090B";  // fixed dark text on every row (incl. the wave row)
              style.transition = `background ${speed}ms linear`;
              style.animation = `flapIn .45s ease ${i * 0.04}s both`;
            } else {
              style.animation = blink
                ? `flapIn .45s ease ${i * 0.04}s both, stockBlink 1s infinite`
                : `flapIn .45s ease ${i * 0.04}s both`;
            }
            // Text stays dark on all cascade rows; only the single-row blink inherits its
            // animated colour.
            const lit = blink;
            const tMain = lit ? "" : "text-[#09090B]";
            const tSub = lit ? "opacity-60" : "text-zinc-400";
            const clickable = !!r.body_slug;
            return (
              <div key={`${r.short}-${p}-${i}`} data-testid={`board-row-${r.short}`}
                onClick={() => clickable && onOpen(r)}
                className={`relative overflow-hidden px-3 sm:px-4 py-2.5 block sm:grid sm:grid-cols-[1.4fr_0.7fr_1.7fr_1.25fr_1.1fr] sm:gap-2 sm:items-center ${clickable ? "cursor-pointer" : "cursor-default"} ${lit ? "shadow-sm" : "hover:bg-sky-50/60"}`}
                style={style}>

                {/* Board */}
                <span className={`relative block text-sm font-bold truncate ${tMain}`}>
                  {r.body}{r.state && <span className={`font-normal ${tSub}`}> · {r.state}</span>}
                </span>

                {/* Round / Phase — round only */}
                <span className="relative block mt-1 sm:mt-0">
                  {r.event?.round_label ? (
                    <span className={`text-sm font-extrabold tracking-tight ${tMain}`}>{r.event.round_label}</span>
                  ) : board.kind === "exam" ? (
                    <span className={`text-[11px] font-black uppercase tracking-wider ${lit ? "opacity-80" : "text-zinc-500"}`}>Exam</span>
                  ) : <span className={`text-[12px] ${tSub}`}>—</span>}
                </span>

                {/* Status = current timeline item (+ seat-matrix link) + tag */}
                <span className="relative block mt-1.5 sm:mt-0 min-w-0">
                  {(() => {
                    const smStep = r.event && /seat\s*matrix|tentative\s*seat/i.test(r.event.activity || "");
                    const sm = smStep && r.sm_rounds ? r.sm_rounds[String(r.event.round)] : null;
                    const text = r.event ? r.event.activity : "Awaiting counselling schedule";
                    const cls = `block text-[13px] font-semibold leading-snug line-clamp-1 ${lit ? "" : r.event ? "text-[#09090B]" : "text-zinc-400"}`;
                    if (sm) return (
                      <button onClick={(e) => { e.stopPropagation(); onData(sm.url); }} data-testid={`sm-link-${r.short}`}
                        title={`Open ${sm.label}`}
                        className={`${cls} text-left underline decoration-dotted underline-offset-2 hover:opacity-80 inline-flex items-center gap-1`}>
                        {text} <ArrowSquareOut size={11} weight="bold" className="shrink-0" />
                      </button>
                    );
                    return <span className={cls}>{text}</span>;
                  })()}
                  <span className="mt-1 inline-block"><StatusPill status={r.status} blink={lit} /></span>
                </span>

                {/* Window */}
                <span className={`relative block mt-1.5 sm:mt-0 font-mono tabular-nums ${waveOn ? "text-white font-bold text-[12.1px]" : `text-[11px] ${lit ? "" : "text-zinc-500"}`}`}>
                  {r.event ? (
                    <>
                      <span className="inline-flex items-center gap-1"><CalendarBlank size={waveOn ? 13 : 12} weight="bold" className={waveOn ? "text-white" : lit ? "" : "text-[#6043EF]"} /> {rangeLabel(r.event)}</span>
                      {r.event.time && <span className={`block mt-0.5 ${waveOn ? "text-white font-bold text-[11px]" : `text-[10px] ${lit ? "opacity-70" : "text-zinc-400"}`}`}>{r.event.time}</span>}
                    </>
                  ) : "—"}
                </span>

                <span className="relative flex items-center gap-2 mt-1.5 sm:mt-0 flex-wrap">
                  {r.applyExt && (
                    <a href={r.applyExt} target="_blank" rel="noreferrer" onClick={(e) => e.stopPropagation()}
                      data-testid={`apply-ext-${r.short}`} title={r.apply_link_external_label || "External application / registration portal"}
                      className="inline-flex items-center gap-1 h-7 px-3 rounded-full text-[10px] font-black uppercase tracking-wider bg-red-600 text-white shadow shadow-red-500/30 transition-transform hover:scale-105">
                      Apply <ArrowSquareOut size={11} weight="bold" />
                    </a>
                  )}
                  {r.apply ? (
                    <a href={r.apply} target="_blank" rel="noreferrer" onClick={(e) => e.stopPropagation()}
                      data-testid={`apply-btn-${r.short}`}
                      className={`inline-flex items-center gap-1 h-7 px-3 rounded-full text-[10px] font-black uppercase tracking-wider transition-transform hover:scale-105 ${lit ? "bg-red-600 text-white ring-1 ring-white/60" : "bg-red-600 text-white shadow shadow-red-500/30"}`}>
                      Apply <ArrowSquareOut size={11} weight="bold" />
                    </a>
                  ) : (() => {
                    const dr = (r.data_rounds || []).filter((d) => !d.gate_date || d.gate_date <= refDate);
                    if (dr.length) return <DataRoundCell rounds={dr} colored={false} onData={onData} />;
                    return clickable ? (
                      <span className={`inline-flex items-center gap-0.5 text-[10px] font-black uppercase tracking-wider ${lit ? "" : "text-[#6043EF]"}`}>
                        View schedule <CaretRight size={11} weight="bold" />
                      </span>
                    ) : <span className={`text-[10px] ${tSub}`}>—</span>;
                  })()}
                </span>
              </div>
            );
          })}
        </div>
      </div>

      {/* auto-advance progress bar */}
      {pages > 1 && (
        <div className="mt-1.5 h-1 w-full rounded-full bg-zinc-200/70 overflow-hidden" data-testid="board-autoprogress">
          <div key={`${p}-${paused || userPaused}`} className="h-full rounded-full bg-gradient-to-r from-[#6043EF] to-sky-400"
            style={{ animation: (paused || userPaused) ? "none" : "autoprog 8s linear", width: (paused || userPaused) ? "100%" : undefined, opacity: (paused || userPaused) ? 0.3 : 1 }} />
        </div>
      )}

      {/* pagination + play/pause */}
      {pages > 1 && (
        <div className="flex items-center justify-center gap-1.5 mt-3" data-testid="board-pagination">
          <button onClick={() => setPage(Math.max(0, p - 1))} disabled={p === 0} data-testid="board-prev"
            className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-white border border-zinc-200 text-zinc-600 disabled:opacity-40 hover:border-[#6043EF] hover:text-[#6043EF] transition-colors">
            <CaretLeft size={13} weight="bold" />
          </button>
          {Array.from({ length: pages }).map((_, i) => (
            <button key={i} onClick={() => setPage(i)} data-testid={`board-page-${i}`}
              className={`h-7 min-w-[28px] px-1 rounded-full text-[11px] font-bold transition-all ${i === p ? "bg-[#6043EF] text-white shadow-md shadow-[#6043EF]/30 scale-110" : "bg-white border border-zinc-200 text-zinc-500 hover:border-[#6043EF]"}`}>
              {i + 1}
            </button>
          ))}
          <button onClick={() => setPage(Math.min(pages - 1, p + 1))} disabled={p === pages - 1} data-testid="board-next"
            className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-white border border-zinc-200 text-zinc-600 disabled:opacity-40 hover:border-[#6043EF] hover:text-[#6043EF] transition-colors">
            <CaretRight size={13} weight="bold" />
          </button>
          <button onClick={() => setUserPaused((v) => !v)} data-testid="board-autoscroll-toggle"
            title={userPaused ? "Resume auto-scroll" : "Pause auto-scroll"} aria-pressed={userPaused}
            className={`ml-1 inline-flex items-center gap-1 h-7 px-2.5 rounded-full text-[10px] font-black uppercase tracking-wider transition-colors ${userPaused ? "bg-amber-100 text-amber-600 hover:bg-amber-200" : "bg-emerald-100 text-emerald-600 hover:bg-emerald-200"}`}>
            {userPaused ? <Play size={12} weight="fill" /> : <Pause size={12} weight="fill" />}
            {userPaused ? "Play" : "Pause"}
          </button>
          <span className="ml-1 inline-flex items-center gap-1 text-[10px] font-semibold text-zinc-400 tabular-nums">
            {p * PAGE + 1}–{Math.min((p + 1) * PAGE, rows.length)} of {rows.length}
          </span>
        </div>
      )}
    </div>
  );
}

function DataRoundCell({ rounds, colored, onData }) {
  const [open, setOpen] = useState(false);
  const [first, ...rest] = rounds;
  return (
    <span className="relative flex items-center gap-1 flex-wrap" data-testid="data-round-cell">
      <button onClick={(e) => { e.stopPropagation(); onData(first.url); }} data-testid={`data-round-${first.round}`}
        className={`inline-flex items-center gap-1 h-7 px-2.5 rounded-full text-[10px] font-black uppercase tracking-wider transition-transform hover:scale-105 ${colored ? "bg-white text-emerald-700" : "bg-emerald-500 text-white shadow shadow-emerald-500/30"}`}>
        {first.label} data <CaretRight size={10} weight="bold" />
      </button>
      {rest.length > 0 && (
        <span className="relative">
          <button onClick={(e) => { e.stopPropagation(); setOpen((o) => !o); }} data-testid="data-round-more"
            className={`h-7 px-2 rounded-full text-[10px] font-bold transition-colors ${colored ? "bg-white/30 text-white hover:bg-white/40" : "bg-zinc-100 text-zinc-600 hover:bg-zinc-200"}`}>
            +{rest.length} more
          </button>
          {open && (
            <span className="absolute right-0 top-8 z-30 w-44 rounded-xl border border-zinc-200 bg-white shadow-xl p-1.5 flex flex-col gap-0.5" onClick={(e) => e.stopPropagation()} data-testid="data-round-more-list">
              {rest.map((d) => (
                <button key={d.round} onClick={(e) => { e.stopPropagation(); onData(d.url); }} data-testid={`data-round-${d.round}`}
                  className="text-left text-[11px] font-semibold text-zinc-600 hover:text-[#6043EF] hover:bg-zinc-50 rounded px-2 py-1 transition-colors">
                  {d.label} data →
                </button>
              ))}
            </span>
          )}
        </span>
      )}
    </span>
  );
}

function StatusPill({ status, blink }) {
  const map = {
    live: { label: "Live", cls: blink ? "bg-blue-700 text-white ring-white/70" : "bg-blue-100 text-blue-700 ring-blue-300", dot: true },
    upcoming: { label: "Upcoming", cls: "bg-amber-100 text-amber-700 ring-amber-300", dot: false },
    scheduled: { label: "Scheduled", cls: "bg-indigo-100 text-indigo-700 ring-indigo-300", dot: false },
  };
  const s = map[status] || map.upcoming;
  return (
    <span className={`relative overflow-hidden shrink-0 whitespace-nowrap inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[10px] font-black uppercase tracking-wider ring-1 ${s.cls}`}>
      {s.dot
        ? <span className="inline-block w-1.5 h-1.5 rounded-full bg-current" style={{ animation: "pulseDot 1s ease-in-out infinite" }} />
        : status === "completed" ? <CheckCircle size={11} weight="fill" /> : null}
      {s.label}
      {status === "live" && (
        <span aria-hidden className="absolute inset-y-0 -left-2 w-5 skew-x-[-20deg] bg-white/50 pointer-events-none" style={{ animation: "shine 2.4s ease-in-out infinite" }} />
      )}
    </span>
  );
}
